import { useUserStore } from '@/stores'
import { localStream } from '@stream'
import { messenger } from '@communication'
import { useAppStore } from '../stores'
import { sleep } from '@/utils'

export default function useLocalStream() {
  const stream = localStream
  const video = localStream.video
  const audio = localStream.audio

  const getVideoTrack = () => {
    return stream.getTrackByType('video')
  }

  const getTrackCapabilities = (videoTrack) => {
    // getCapabilities is missing in older Firefox and returns {} for tracks that do
    // not come from local hardware (e.g. the Android connector)
    try {
      const capabilities = videoTrack.getCapabilities?.()
      if (capabilities?.width?.max && capabilities?.height?.max) return capabilities
    } catch (error) {}
    return null
  }

  const getMaxResolution = (videoTrack) => {
    const capabilities = getTrackCapabilities(videoTrack)
    const width = Math.max(capabilities.width.max, capabilities.height.max, 1920)
    const height = Math.min(capabilities.width.max, capabilities.height.max, 1080)
    return { width, height }
  }

  const getMaxResolutionFromCurrent = (videoTrack) => {
    const { width: maxWidth, height: maxHeight } = getMaxResolution(videoTrack)
    const { width: cameraWidth, height: cameraHeight } = getCurrentResolution(videoTrack)
    const ratio = Math.min(maxWidth / cameraWidth, maxHeight / cameraHeight)
    const idealWidth = Math.floor(cameraWidth * ratio)
    const idealHeight = Math.floor(cameraHeight * ratio)
    return { width: idealWidth, height: idealHeight }
  }

  const getCurrentResolution = (videoTrack) => {
    const { width: originWidth, height: originHeight } = videoTrack.getSettings()
    const width = Math.max(originWidth, originHeight)
    const height = Math.min(originWidth, originHeight)
    return { width, height }
  }

  const isMaxResolution = (videoTrack) => {
    // Without capability data a resolution bump cannot be attempted safely
    if (!getTrackCapabilities(videoTrack)) return true
    const { width: maxWidth, height: maxHeight } = getMaxResolution(videoTrack)
    const { width: cameraWidth, height: cameraHeight } = getCurrentResolution(videoTrack)
    return cameraWidth === maxWidth || cameraHeight === maxHeight
  }

  const changeResolution = async ({ width, height, deviceId, facingMode }, isRetry = false) => {
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: width },
          height: { ideal: height },
          // as in client.settings.js: no cropping, or a portrait camera ends up square after a snapshot
          resizeMode: 'none',
          deviceId: { ideal: deviceId },
          facingMode: { ideal: facingMode }
        }
      })
      return stream.watchEnded(newStream.getTracks()[0])
    } catch (error) {
      // Firefox (notably on Windows) may not have released the camera yet right
      // after the previous track was stopped
      if ((error.name === 'NotReadableError' || error.name === 'AbortError') && !isRetry) {
        await sleep(300)
        return changeResolution({ width, height, deviceId, facingMode }, true)
      }
      throw error
    }
  }

  const getSnapshotUsingCanvas = async () => {
    let data
    let thumbnailData
    const localVideo = document.getElementById('local-video')
    if (!localVideo) throw new Error('local-video element not found')
    const currentTrack = localStream.video.getTrack()
    const settings = currentTrack?.getSettings() ?? {}
    const isLandscape = screen.orientation.type.includes('landscape')

    // No usable camera track (missing, ended after an earlier failure, or not
    // hardware-backed) — capture whatever the video element currently renders
    if (!currentTrack || currentTrack.readyState === 'ended' || !settings.width) {
      const width = localVideo.videoWidth || 1280
      const height = localVideo.videoHeight || 720
      return {
        thumbnailData: createSnapshot(localVideo, width, height, true),
        data: createSnapshot(localVideo, width, height, false),
        width,
        height,
      }
    }

    const { deviceId, facingMode } = settings
    const { width: cameraWidth, height: cameraHeight } = getCurrentResolution(currentTrack)
    let imageWidth = isLandscape ? cameraWidth : cameraHeight
    let imageHeight = isLandscape ? cameraHeight : cameraWidth

    if (!currentTrack.enabled || isMaxResolution(currentTrack)) {
      thumbnailData = createSnapshot(localVideo, imageWidth, imageHeight, true)
      data = createSnapshot(localVideo, imageWidth, imageHeight, false)
      return {
        thumbnailData,
        data,
        width: imageWidth,
        height: imageHeight,
      }
    }

    // Bump the camera to its maximum resolution for the capture. The current track
    // must be stopped first (the same camera cannot be opened twice at different
    // resolutions), and replaceTrack keeps the peer connection and the local
    // preview on a live track instead of a stopped one for the whole capture.
    let activeTrack = currentTrack
    try {
      const { width: idealWidth, height: idealHeight } = getMaxResolutionFromCurrent(currentTrack)
      currentTrack.stop()
      const snapshotTrack = await changeResolution({ width: idealWidth, height: idealHeight, deviceId, facingMode })
      localStream.video.replaceTrack(activeTrack, snapshotTrack)
      activeTrack = snapshotTrack
      await localVideo.play().catch(() => {})
      await sleep(1500)

      // Draw at the resolution the snapshot track actually delivers
      const { width: snapshotWidth, height: snapshotHeight } = getCurrentResolution(snapshotTrack)
      imageWidth = isLandscape ? snapshotWidth : snapshotHeight
      imageHeight = isLandscape ? snapshotHeight : snapshotWidth
      thumbnailData = createSnapshot(localVideo, imageWidth, imageHeight, true)
      data = createSnapshot(localVideo, imageWidth, imageHeight, false)
    } finally {
      // Always restore a live track at the original resolution — this also revives
      // the camera when any step above failed, otherwise the stream keeps a dead
      // track and every following snapshot fails too
      try {
        activeTrack.stop()
        const restoredTrack = await changeResolution({ width: cameraWidth, height: cameraHeight, deviceId, facingMode })
        localStream.video.replaceTrack(activeTrack, restoredTrack)
        await localVideo.play().catch(() => {})
      } catch (restoreError) {
        console.error('[getSnapshotUsingCanvas] Failed to restore camera after snapshot:', restoreError)
      }
    }

    return {
      thumbnailData,
      data,
      width: imageWidth,
      height: imageHeight,
    }
  }

  const restartMedia = async ({ restartVideo = true, restartAudio = true } = {}) => {
    const currentVideoTrack = video.getTrack()
    const currentAudioTrack = audio.getTrack()
    const localVideo = document.getElementById('local-video')
    const app = useAppStore()
    const user = useUserStore()
    const currentVideoDevice = currentVideoTrack.getSettings().deviceId
    const currentAudioDevice = currentAudioTrack.getSettings().deviceId
    const { idealConstraints } = window.__INITIAL_STATE__.app
    const constraints = { video: false, audio: false }
    if (restartVideo && currentVideoTrack) {
      currentVideoTrack.stop()
      constraints.video = {
        ...idealConstraints.camera,
        deviceId: { exact: currentVideoDevice },
      }
    }

    if (restartAudio && currentAudioTrack) {
      currentAudioTrack.stop()
      constraints.audio = {
        ...idealConstraints.microphone,
        deviceId: { exact: currentAudioDevice },
      }
    }
    const newStream = await navigator.mediaDevices.getUserMedia(constraints)

    const [newVideoTrack] = newStream.getVideoTracks().map(stream.watchEnded)
    const [newAudioTrack] = newStream.getAudioTracks().map(stream.watchEnded)
    if (restartVideo && newVideoTrack) {
      video.replaceTrack(currentVideoTrack, newVideoTrack)
      app.transition('CAMERA_AVAILABLE')
      user.profile.camera = 'on'

      if (app.state.matches(`app.camera.available.muted`)) {
        await app.transition(`MUTE_CAMERA`)
      }
      if (currentVideoTrack && app.state.matches('app.view.meeting')) {
        await localVideo.play()
      }
    }

    if (restartAudio && newAudioTrack) {
      audio.replaceTrack(currentAudioTrack, newAudioTrack)
      app.transition('MICROPHONE_AVAILABLE')
      user.profile.microphone = 'on'

      if (app.state.matches('app.microphone.available.muted')) {
        await app.transition(`MUTE_MICROPHONE`)
      }
    }
  }

  const createSnapshot = (videoElement, width, height, isThumbnail) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = width
    canvas.height = height
    ctx.drawImage(videoElement, 0, 0, width, height)
    const data = canvas.toDataURL('image/jpeg', isThumbnail ? 0.1 : 1)
    return data
  }

  const changeDevice = async (...args) => {
    await stream.changeDevice(...args)
  }

  const addTrack = async device => {
    const result = await stream.addTrack(device)
    if (device === 'camera') {
      const hasTorchCapability = await stream.hasTorchCapability()
      const user = useUserStore()
      user.setTorchCapability(hasTorchCapability)
    }
    return result
  }

  const addAndroidTrack = async device => {
    await stream.addAndroidTrack(device)
  }

  const replaceDevice = async (...args) => {
    await stream.replaceDevice(...args)
    const hasTorchCapability = await stream.hasTorchCapability()
    const user = useUserStore()
    user.setTorchCapability(hasTorchCapability)
    if (hasTorchCapability) {
      user.setTorchStatus(false)
    }
  }

  const toggleTorch = async status => {
    const currentTorchStatus = await stream.toggleTorch(status)
    const user = useUserStore()
    user.setTorchStatus(currentTorchStatus)
    return currentTorchStatus
  }

  return {
    // inherited
    stream: stream,
    audio: audio.stream,
    video: video.stream,
    audioUpdatedAt: audio.updatedAt,
    videoUpdatedAt: video.updatedAt,

    mute: stream.mute,
    unmute: stream.unmute,

    // local
    getVideoTrack,
    getSnapshot: getSnapshotUsingCanvas,
    addTrack,
    addAndroidTrack,
    replaceDevice,
    changeDevice,
    toggleTorch,
    restartMedia,
  }
}
